/**
 * Casas del menú principal y permisos reales que las desbloquean.
 * Preferimos ocultar el ítem si el usuario no puede ver contenido útil en esa ruta.
 * Dueño (todas las keys) ve el menú completo.
 *
 * Alinear con los `canView` de cada página de dominio.
 */

export type MenuSection = 'primary' | 'more';

export interface MenuHouseDefinition {
  id: string;
  /** Clave i18n (`menu.*`). */
  labelKey: string;
  to: string;
  iconPath: string;
  section: MenuSection;
  /** Basta con uno. */
  anyPermission: string[];
  /** Solo el dueño (API requireOwner). Las keys no desbloquean la casa. */
  ownerOnly?: boolean;
  activePrefixes?: string[];
  hidden?: boolean;
}

/** Orden fijo: casas del día a día + Más. */
export const MENU_HOUSES: MenuHouseDefinition[] = [
  {
    id: 'summary',
    labelKey: 'menu.summary',
    to: '/',
    iconPath: 'M3 13h8V3H3v10zm10 8h8V3h-8v18zM3 21h8v-6H3v6z',
    section: 'primary',
    anyPermission: ['dashboard.view'],
  },
  {
    id: 'clinics',
    labelKey: 'menu.clinics',
    to: '/clinics',
    iconPath: 'M3 21V7l9-4 9 4v14M9 21V12h6v9',
    section: 'primary',
    anyPermission: ['dashboard.view'],
    activePrefixes: ['/clinics'],
  },
  {
    id: 'finances',
    labelKey: 'menu.finances',
    to: '/finances',
    iconPath: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
    section: 'primary',
    anyPermission: ['metrics.caja', 'metrics.tendencia', 'metrics.ranking_planes'],
    activePrefixes: ['/finances'],
  },
  {
    id: 'cashMovements',
    labelKey: 'menu.cashMovements',
    to: '/caja/movimientos',
    iconPath: 'M3 7h18M6 7v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M9 13h6',
    section: 'primary',
    anyPermission: ['metrics.caja'],
    activePrefixes: ['/caja/movimientos'],
    hidden: true,
  },
  {
    id: 'ownership',
    labelKey: 'menu.ownership',
    to: '/ownership',
    iconPath: 'M4 19V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v14M8 17h8M9 7h6M9 11h6M3 21h18',
    section: 'primary',
    anyPermission: ['permissions.manage'],
    activePrefixes: ['/ownership'],
    ownerOnly: true,
  },
  {
    id: 'patients',
    labelKey: 'menu.patients',
    to: '/patients',
    iconPath:
      'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
    section: 'primary',
    anyPermission: ['metrics.pacientes', 'metrics.recurrencia', 'metrics.no_volvieron'],
    activePrefixes: ['/patients'],
  },
  {
    id: 'professionals',
    labelKey: 'menu.professionals',
    to: '/professionals',
    iconPath: 'M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM12 14a7 7 0 0 0-7 7h14a7 7 0 0 0-7-7z',
    section: 'primary',
    anyPermission: ['metrics.profesionales'],
    activePrefixes: ['/professionals'],
  },
  {
    id: 'team',
    labelKey: 'menu.team',
    to: '/team',
    iconPath:
      'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
    section: 'primary',
    anyPermission: ['assistants.manage', 'permissions.manage'],
    activePrefixes: ['/team', '/assistants', '/permissions'],
    ownerOnly: true,
  },
  {
    id: 'communications',
    labelKey: 'menu.communications',
    to: '/communications',
    iconPath:
      'M3 8l7.89 5.26a2 2 0 0 0 2.22 0L21 8M5 19h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2z',
    section: 'more',
    // La página exige manage para listar/operar; view solo no aporta contenido útil.
    anyPermission: ['communications.manage'],
  },
  {
    id: 'subscription',
    labelKey: 'menu.subscription',
    to: '/subscription',
    iconPath:
      'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    section: 'more',
    anyPermission: ['metrics.plan_billing', 'metrics.plan_recaudacion'],
    activePrefixes: ['/subscription'],
  },
  {
    id: 'operations',
    labelKey: 'menu.operations',
    to: '/operations',
    iconPath:
      'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
    section: 'more',
    anyPermission: ['metrics.calidad', 'reports.view', 'reports.export'],
    activePrefixes: ['/operations'],
  },
];

export function canSeeMenuHouse(
  hasPermission: (key: string) => boolean,
  house: MenuHouseDefinition,
  isOwner = false
): boolean {
  if (house.hidden) return false;
  if (house.ownerOnly) return isOwner;
  return house.anyPermission.some((key) => hasPermission(key));
}

/** `hasPermission` a partir de las keys de un rol (preview / tests). */
export function hasPermissionFromKeys(keys: readonly string[]): (key: string) => boolean {
  const set = new Set(keys);
  return (key: string) => set.has(key);
}

export interface MenuHousePreviewItem extends MenuHouseDefinition {
  visible: boolean;
}

/**
 * Casas del menú real (sin las ocultas a propósito) con visible/oculto según el rol.
 * Misma regla que MainShell (`canSeeMenuHouse`).
 */
export function previewMenuHouses(keys: readonly string[]): {
  primary: MenuHousePreviewItem[];
  more: MenuHousePreviewItem[];
} {
  const hasPermission = hasPermissionFromKeys(keys);
  const toPreview = (section: MenuSection) =>
    MENU_HOUSES.filter((house) => house.section === section && !house.hidden).map((house) => ({
      ...house,
      visible: canSeeMenuHouse(hasPermission, house, false),
    }));
  return { primary: toPreview('primary'), more: toPreview('more') };
}

/** `/team` no tiene contenido propio: Usuarios, o Roles si no hay `assistants.manage`. */
export function resolveTeamPath(hasPermission: (key: string) => boolean): string {
  if (hasPermission('assistants.manage')) return '/team/assistants';
  if (hasPermission('permissions.manage')) return '/team/roles';
  return '/team';
}

export function visibleMenuHouses(
  hasPermission: (key: string) => boolean,
  section?: MenuSection,
  isOwner = false
): MenuHouseDefinition[] {
  return MENU_HOUSES.filter((house) => {
    if (section && house.section !== section) return false;
    return canSeeMenuHouse(hasPermission, house, isOwner);
  }).map((house) => (house.id === 'team' ? { ...house, to: resolveTeamPath(hasPermission) } : house));
}

/** Ids del bottom nav mobile (orden fijo). El resto vive en el drawer. */
export const MOBILE_BOTTOM_NAV_HOUSE_IDS = ['summary', 'clinics', 'finances'] as const;

/** Clave i18n de la casa que corresponde a un path (CTA “Ir a Finanzas”). */
export function houseLabelKeyForPath(path: string): string | null {
  const pathname = path.split('?')[0] || '/';
  if (pathname === '/') return 'menu.summary';
  const house = MENU_HOUSES.find((item) => isMenuHouseActive(pathname, item));
  return house?.labelKey ?? null;
}

export function isMenuHouseActive(pathname: string, item: MenuHouseDefinition): boolean {
  if (item.to === '/') {
    return pathname === '/';
  }
  if (pathname === item.to) {
    return true;
  }
  return (item.activePrefixes ?? []).some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function isMoreSectionPath(pathname: string): boolean {
  return (
    pathname.startsWith('/communications') ||
    pathname.startsWith('/subscription') ||
    pathname.startsWith('/operations')
  );
}

/** Casas primary que no van en el bottom nav (siguen en drawer/sidebar). */
export function visibleDrawerExtraPrimary(
  hasPermission: (key: string) => boolean,
  isOwner = false
): MenuHouseDefinition[] {
  const bottom = new Set<string>(MOBILE_BOTTOM_NAV_HOUSE_IDS);
  return visibleMenuHouses(hasPermission, 'primary', isOwner).filter((h) => !bottom.has(h.id));
}

/** Primera casa visible (para redirects cuando `/` no aplica). */
export function firstVisibleHousePath(
  hasPermission: (key: string) => boolean,
  isOwner = false
): string | null {
  return visibleMenuHouses(hasPermission, undefined, isOwner)[0]?.to ?? null;
}

/**
 * Home del panel post-login / click logo / pegar `/` (US-122).
 * Sigue el menú efectivo: `dashboard.view` → `/`; si no, primera casa visible
 * (Finanzas, Pacientes, …). No usa `isOwner`: el dueño ya tiene todas las keys,
 * y al simular un rol el menú oculta Resumen — `/` tiene que ir a esa primera casa.
 * Sin casas → `/` (MainShell muestra sin permisos).
 * `isOwner` se acepta por compatibilidad de call-sites y no cambia el resultado.
 */
export function resolvePanelHomePath(
  hasPermission: (key: string) => boolean,
  _isOwner = false
): string {
  if (hasPermission('dashboard.view')) {
    return '/';
  }
  return firstVisibleHousePath(hasPermission, _isOwner) ?? '/';
}

/** Hay al menos una casa de menú visible (asistente sin rol → false). */
export function hasPanelMenuAccess(hasPermission: (key: string) => boolean, isOwner = false): boolean {
  return visibleMenuHouses(hasPermission, undefined, isOwner).length > 0;
}

/**
 * Permisos que habilitan el hub de una clínica (detalle `/clinics/:id`).
 * No exige dashboard.view: un asistente de finanzas puede entrar por deep link.
 */
export const CLINIC_HUB_ANY_PERMISSIONS = [
  'dashboard.view',
  'metrics.caja',
  'metrics.tendencia',
  'metrics.ranking_planes',
  'metrics.pacientes',
  'metrics.recurrencia',
  'metrics.no_volvieron',
  'metrics.profesionales',
  'metrics.turnos',
] as const;

export function canAccessClinicHub(hasPermission: (key: string) => boolean): boolean {
  return CLINIC_HUB_ANY_PERMISSIONS.some((key) => hasPermission(key));
}
